import React, { useState } from 'react';
import { useSubscription } from '../../context/SubscriptionContext';
import { FREE_TIER_DAILY_LIMIT } from '../../types/subscription';
import UpgradePrompt from './UpgradePrompt';

interface RecordingLimitProps {
  className?: string;
}

const RecordingLimit: React.FC<RecordingLimitProps> = ({ className = '' }) => {
  const { plan, getRecordingsRemaining, loading, subscription } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Don't show for paid plans
  if (loading || plan !== 'free') {
    return null;
  }

  const remaining = getRecordingsRemaining();
  const bonusCredits = subscription?.bonusCredits || 0;
  const totalAvailable = FREE_TIER_DAILY_LIMIT + bonusCredits;
  const used = totalAvailable - remaining;
  const percentage = (used / totalAvailable) * 100;

  const getStatusColor = () => {
    if (remaining === 0) return 'text-red-600';
    if (remaining <= totalAvailable * 0.33) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getBarColor = () => {
    if (remaining === 0) return 'bg-red-500';
    if (remaining <= totalAvailable * 0.33) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <>
      <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Daily Recordings
          </span>
          <span className={`text-sm font-semibold ${getStatusColor()}`}>
            {remaining} left
          </span>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
          <div
            className={`h-full ${getBarColor()} transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          />
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {used} of {totalAvailable} used today
            {bonusCredits > 0 && <span className="text-green-600"> (+{bonusCredits} bonus)</span>}
          </span>
          <button
            onClick={() => setShowUpgradeModal(true)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Get unlimited
          </button>
        </div>

        {/* Warning when limit reached */}
        {remaining === 0 && (
          <div className="mt-3 p-3 bg-red-50 rounded-lg">
            <p className="text-sm text-red-700">
              You've reached your daily limit. Upgrade to continue practicing!
            </p>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="mt-2 text-sm font-medium text-red-600 hover:text-red-800"
            >
              Upgrade Now →
            </button>
          </div>
        )}
      </div>

      <UpgradePrompt
        feature="unlimited_recordings"
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </>
  );
};

export default RecordingLimit;
